/**
 * Generic App Storage Hook
 * Provides save/load/delete functionality for IndexedDB
 * Eliminates need to duplicate storage.ts for each app
 */

import type { SavedPage } from '../database/db';

export interface SaveData<TInput = unknown, TResponse = unknown> extends Record<string, unknown> {
  inputs: TInput;
  results?: TResponse;
}

/**
 * Generic hook to save/load app data to IndexedDB
 *
 * @param appName - Unique name for the app (e.g., 'iSteelElement', 'iSDOFSolver')
 * @param version - App version (e.g., '1.0.0')
 *
 * @example
 * const { saveDataToDB, deleteData, updateData } = useAppStorage<MyInput, MyResponse>('iMyApp', '1.0.0');
 */
export function useAppStorage<TInput = unknown, TResponse = unknown>(appName: string, version: string = '1.0.0') {
  const saveDataToDB = async (project: string, title: string, inputs: TInput, results?: TResponse, notes?: string) => {
    const { db } = await import('../database/db');

    const savePayload: SaveData<TInput, TResponse> = {
      inputs,
      results
    };

    return db.savedPages.add({
      appName,
      project,
      url: window.location.href,
      title,
      data: savePayload,
      timestamp: Date.now(),
      metadata: {
        version,
        userAgent: navigator.userAgent,
        notes
      }
    });
  };

  const deleteData = async (id: number) => {
    const { db } = await import('../database/db');
    await db.savedPages.delete(id);
  };

  const updateData = async (id: number, changes: Partial<SavedPage>) => {
    const { db } = await import('../database/db');
    await db.savedPages.update(id, changes);
  };

  return { saveDataToDB, deleteData, updateData };
}
